import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { CustomCloudSavedIcon } from '@nesso/ui/icons/custom-cloud-saved';
import { cn } from '@nesso/ui/lib/utils';
import { PopoverContent } from '@nesso/ui/primitives/popover';

import { sessionByIdOptions } from '@/entities/session';
import {
    SAVING_INDICATOR_TIMEOUT,
    useIsGenerating,
    useIsGenerationAvailable,
} from '@/entities/session-note';
import { formatNumericDateTime } from '@/shared/lib/time-utils';
import { PopoverTooltipButton } from '@/shared/ui/common/popover-tooltip-button';

import { useSessionId } from '../../../../../model/use-session-id';

interface Props {
    isSaving?: boolean;
    lastSavedAt?: Date;
}

export function NoteSavingIndicator({ isSaving = false, lastSavedAt }: Props) {
    const [showSavedText, setShowSavedText] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const prevIsSaving = useRef(isSaving);

    const sessionId = useSessionId();
    const { data: session } = useQuery(sessionByIdOptions(sessionId));
    const isGenerating = useIsGenerating(sessionId);
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);
    const isDisabled = isGenerating || (isGenerationAvailable && !session?.currentNote);

    useEffect(() => {
        if (prevIsSaving.current && !isSaving) {
            setShowSavedText(true);
            const timer = setTimeout(() => {
                setShowSavedText(false);
            }, SAVING_INDICATOR_TIMEOUT);
            return () => clearTimeout(timer);
        }
        prevIsSaving.current = isSaving;
    }, [isSaving]);

    if (isSaving) {
        return (
            <div className="flex items-center gap-2 text-foreground px-3 font-medium">
                <RefreshCw className="h-4 w-4" />
                <span className="max-xs:sr-only text-sm">Saving...</span>
            </div>
        );
    }

    const formattedLastSaved = lastSavedAt
        ? `Last updated ${formatNumericDateTime(lastSavedAt)}`
        : null;

    return (
        <PopoverTooltipButton
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            tooltipContent={<p className="text-sm">All changes saved</p>}
            tooltipContentProps={{
                side: 'top',
                align: 'start',
                alignOffset: -2,
                showArrow: false,
            }}
            popoverContent={
                <PopoverContent
                    className="flex w-auto flex-col gap-1 p-3"
                    side="top"
                    align="start"
                    alignOffset={-2}
                >
                    <p className="text-sm font-medium">Note saved</p>
                    {formattedLastSaved && (
                        <p className="text-sm text-muted-foreground">{formattedLastSaved}</p>
                    )}
                </PopoverContent>
            }
            wrapPopoverContent={false}
            buttonProps={{
                variant: 'ghost',
                className: cn('gap-0 !p-3 text-muted-foreground', popoverOpen && 'bg-accent/50'),
                disabled: isDisabled,
            }}
        >
            <CustomCloudSavedIcon className="h-4 w-4" />
            <span
                className={cn(
                    'max-xs:sr-only text-sm text-foreground transition-all duration-300 overflow-hidden whitespace-nowrap font-medium',
                    showSavedText ? 'max-w-[100px] opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0',
                )}
            >
                Saved
            </span>
        </PopoverTooltipButton>
    );
}
